<!-- Figma: 3DWay / Advertising_web_last (28331:31198). Section comments name the Figma layers. -->
<app-discover-topbar [active]="'references'" (accountClicked)="onAccountClick()"></app-discover-topbar>

<main class="discover" id="discover-main">

  <!-- Text + Search + Reference -->
  <section class="hero">
    <div class="hero-copy">
      <h1>Find References.<br>Start Creating.</h1>
      <p class="intro">Explore AI-generated concepts reviewed by 3D artists. Find the forms, materials and details that move your next project forward.</p>

      <!-- Each chip shows its collection in the featured block below. -->
      <div class="try-row">
        <span class="try-row__label">Try</span>
        <button *ngFor="let item of tryQueries" class="try-row__chip" type="button" (click)="showCollection(item.featured)">{{ item.label }}</button>
      </div>

      <!-- Submit searches the backend and shows the results in the featured block below. -->
      <!-- #heroSearch is what the dock watches: dock hidden while this form is on screen. -->
      <form #heroSearch class="hero-search" role="search" (submit)="onHeroSearchSubmit($event)">
        <label class="sr-only" for="discover-hero-query">Search references</label>
        <input
          id="discover-hero-query"
          class="hero-search__input"
          name="query"
          type="text"
          maxlength="120"
          autocomplete="off"
          [placeholder]="searchPlaceholder"
          [(ngModel)]="heroPrompt">
        <button class="hero-search__submit" type="submit">Search</button>
      </form>
      <!-- Figma 28400:20024 (designer, 2026-09-18): the dock note repeated under the hero search. -->
      <!-- "Sign in" opens the same popup as Account (owner, 2026-09-18). -->
      <p class="hero-note">Free to explore during beta. <button class="note-link" type="button" (click)="onAccountClick()">Sign in</button> when you find something to keep.</p>
    </div>

    <figure class="hero-art">
      <div class="hero-art__stage">
        <div class="device-monitor">
          <span class="device-monitor__camera" aria-hidden="true"></span>
          <img class="device-monitor__screen" [src]="imageUrl(hero.landscape)" [alt]="hero.landscape.alt" width="1600" height="679" fetchpriority="high">
        </div>
        <div class="device-phone">
          <span class="device-phone__notch" aria-hidden="true"></span>
          <span class="device-phone__screen">
            <img [src]="imageUrl(hero.portrait)" [alt]="hero.portrait.alt" width="400" height="865">
          </span>
        </div>
      </div>
      <figcaption class="hero-art__caption">
        <span class="hero-art__title" [title]="hero.caption"><span class="hero-art__title-full">{{ hero.caption }}</span><span class="hero-art__title-short">{{ hero.captionShort }}</span></span>
        <a class="text-link" href="#" (click)="showCollection(hero.featured, $event)">{{ hero.exploreLabel }}</a>
      </figcaption>
    </figure>
  </section>

  <hr class="rule">

  <!-- Group 5259: featured block (a bundled collection or the search results) + "Made with AI". -->
  <!-- #featuredSection is where the chips and a search scroll to. The body is a one-item *ngFor: -->
  <!-- a new block (trackBy key) re-creates it, which runs the fade-in; a search updates it in place. -->
  <section #featuredSection class="featured" aria-live="polite" [attr.aria-busy]="featured.status === 'loading'">
    <div
      *ngFor="let block of featuredList; trackBy: trackByFeaturedKey"
      class="featured__body"
      [class.featured__body--enter]="featuredSwitched">
      <div class="featured__head">
        <h2 class="featured__title" [class.featured__title--long]="block.headline.length > 24">{{ block.headline }}<br>{{ block.subline }}</h2>
        <p class="featured__aside" *ngIf="block.aside">{{ block.aside }}</p>
      </div>

      <div class="card-grid" *ngIf="block.status === 'loading'" aria-hidden="true">
        <span *ngFor="let slot of skeletonSlots" class="card card--skeleton"></span>
      </div>

      <ng-container *ngIf="block.status === 'ready'">
        <div class="card-grid">
          <a
            *ngFor="let card of block.cards; trackBy: trackByCardSrc"
            class="card"
            href="#"
            [attr.aria-label]="card.alt"
            (click)="onOpenInLibraryClick(block.query, $event)">
            <img [src]="card.src" [alt]="card.alt" width="480" height="1039" loading="lazy" decoding="async">
            <span class="card__meta" aria-hidden="true">
              <span class="card__title">{{ card.alt }}</span>
              <span class="card__hint">{{ cardHint }}</span>
            </span>
          </a>
        </div>
        <p class="featured__explore">
          <a class="text-link" href="#" (click)="onOpenInLibraryClick(block.query, $event)">Explore all {{ block.countLabel }} references</a>
        </p>
      </ng-container>

      <!-- Nothing found: the brief dialog, prefilled with the query (owner, 2026-09-18). -->
      <div class="featured__action" *ngIf="block.status === 'empty'">
        <button class="pill-button" type="button" (click)="onRequestClick()">Request references</button>
      </div>
      <div class="featured__action" *ngIf="block.status === 'error'">
        <button class="pill-button" type="button" (click)="onRetrySearchClick()">Try again</button>
      </div>
    </div>
  </section>

  <section class="review">
    <h2 class="review__title">Made with AI.<br>Chosen by artists.</h2>
    <div class="review__body">
      <p class="review__lead">A useful starting point.<br>Room for your own ideas.</p>
      <p class="review__text">Our 3D artists review the generated references before they enter the library. You explore the shapes and details, and decide what belongs in your work.<br>Concept references for creative exploration.</p>
    </div>
  </section>

  <!-- Group 5256: light section with collection covers. #keepSection: the dock note turns dark over it. -->
  <section #keepSection class="keep">
    <div class="keep__inner">
      <div class="keep__copy">
        <h2 class="keep__title">Keep the ideas<br>that belong together.</h2>
        <!-- "Save this collection" was here; removed for now (designer, 2026-09-18). -->
        <!-- Figma 28346:32431 (designer, 2026-09-18): the "One Library…" line moved up here from the bottom of the section. -->
        <p class="keep__text">One Library for concept images and rendered-model videos.<br>Save a reference. Build your collection.<br>Share the direction with a link.</p>
      </div>
      <!-- Covers open the collection in the library (sign-in first for guests, like "Explore all"). -->
      <div class="covers">
        <a
          *ngFor="let cover of covers; trackBy: trackByCoverName"
          class="cover"
          href="#"
          [attr.aria-label]="'Open the ' + cover.name + ' collection'"
          (click)="onOpenInLibraryClick(cover.query, $event)">
          <span class="cover__collage">
            <img class="cover__tile cover__tile--main" [src]="imageUrl(cover.cover[0])" [alt]="cover.cover[0].alt" loading="lazy">
            <img class="cover__tile" [src]="imageUrl(cover.cover[1])" [alt]="cover.cover[1].alt" loading="lazy">
            <img class="cover__tile" [src]="imageUrl(cover.cover[2])" [alt]="cover.cover[2].alt" loading="lazy">
          </span>
          <span class="cover__name">{{ cover.name }}</span>
          <span class="cover__count">{{ cover.count }}</span>
        </a>
      </div>
    </div>
  </section>

  <!-- Group 5255 (top): request -->
  <section class="request">
    <div class="request__copy">
      <h2 class="request__title">Your next idea<br>might be new to us.</h2>
      <p class="request__text">Search what’s ready now. If your brief isn’t covered, request new references. We confirm the scope and timing before accepting it.</p>
    </div>
    <div class="request__action">
      <span class="cover__collage cover__collage--ghost" aria-hidden="true"><span></span><span></span><span></span></span>
      <button class="pill-button request__button" type="button" (click)="onRequestClick()">Request references</button>
    </div>
  </section>

  <hr class="rule">

  <!-- Group 5251: FAQ. Items start collapsed (designer, 2026-09-17). -->
  <section class="faq">
    <h2 class="faq__title">A few things<br>worth knowing.</h2>
    <div class="faq__list">
      <details class="faq__item" *ngFor="let item of faq">
        <summary class="faq__question">
          <svg class="faq__arrow" width="17" height="16" viewBox="0 0 16.4256 16" fill="none" aria-hidden="true">
            <path d="M0.715722 5H15.7098C16.1635 5 16.3827 5.55556 16.0512 5.86531L8.55417 12.8716C8.362 13.0512 8.06355 13.0512 7.87138 12.8716L0.374329 5.86531C0.0428832 5.55556 0.262069 5 0.715722 5Z" fill="white" stroke="white" stroke-width="0.428571"/>
          </svg>
          <span>{{ item.question }}</span>
        </summary>
        <p class="faq__answer">{{ item.answer }}</p>
      </details>
    </div>
  </section>

  <!-- Under the fixed dock on desktop (and fades with it); back in the flow on phones where the dock is cramped. -->
  <p #dockNote class="dock-note" [class.is-hidden]="!dockVisible" [class.is-on-light]="dockNoteOnLight"><span #dockNoteText>Free to explore during beta. <button class="note-link" type="button" (click)="onAccountClick()">Sign in</button> when you find something to keep.</span></p>
</main>

<!-- Footer_menu_main: the real dock. Hidden while the hero search is on screen; submits the same search as the hero form. -->
<app-advanced-search-navbar
  [isHidden]="!dockVisible"
  [plate]="true"
  [submitOnlyWithDraft]="true"
  [libraryButtonMode]="'add'"
  [attachmentsEnabled]="false"
  [placeholder]="searchPlaceholder"
  (libraryClicked)="onDockLibraryClick()"
  (accountClicked)="onAccountClick()"
  (submitRequested)="onDockSearchSubmit($event)">
</app-advanced-search-navbar>
